/**
 * The reconciliation rule, as one pure function.
 *
 * ## The rule
 *
 * > **The server's ordered log is the truth. The screen is that truth with the
 * > unacknowledged outbox replayed on top, in outbox order.**
 *
 * The client keeps two states:
 *
 * | | what it is | how it changes |
 * | --- | --- | --- |
 * | `confirmed` | the workspace at the server's version | only ever by applying server-ordered commands: a `commands` delta, a `snapshot`, or the client's own commands once an `ack` has given them versions |
 * | *optimistic* | what the UI renders | `replay(confirmed, outbox)` — recomputed whenever `confirmed` or the outbox changes shape |
 *
 * A local dispatch does not touch `confirmed`. It applies to the optimistic
 * state and appends to the outbox, and the two stay consistent because the
 * optimistic state *is* `replay(confirmed, outbox)` by construction.
 *
 * ## Conflicts
 *
 * There is no merge step, because there is nothing to merge: the server orders
 * everything. When another device's command arrives, it is applied to
 * `confirmed` first, and the local unacked commands are then replayed **after**
 * it. The remote command is therefore older in the total order and the local
 * one wins on any field they both touch — last-write-wins at field
 * granularity, exactly as PLAN §8.3 specifies, with the server's order deciding
 * what "last" means.
 *
 * ## Commands that stop making sense
 *
 * Replay is *tolerant*. If a remote command deleted the recipe a local command
 * edits, the local command's reducer returns `not-found` and it would be
 * rejected by the server too. {@link replay} drops it, keeps going, and reports
 * it — so a single stale edit cannot wedge the queue, and the state on screen
 * is always something the server would agree with.
 *
 * ## Determinism
 *
 * Every input is data and `applyCommand` is pure, so two devices given the same
 * `confirmed` and the same outbox produce deep-equal states. That is the
 * property the tests assert, and it is the reason the server can run the same
 * reducer and reach the same answer.
 */

import type { Command, CommandError, WorkspaceState } from '@beerwright/commands';
import { applyCommand } from '@beerwright/commands';

/** A command replay refused, and why. */
export interface DroppedCommand {
  readonly command: Command;
  readonly error: CommandError;
}

/** The result of replaying an outbox onto a server prefix. */
export interface ReplayResult {
  /** What the UI should render. */
  readonly state: WorkspaceState;
  /** The commands that still apply, in order — the outbox after replay. */
  readonly kept: readonly Command[];
  /** The commands that no longer apply. Already removed from {@link kept}. */
  readonly dropped: readonly DroppedCommand[];
}

/**
 * Apply `commands` to `base`, skipping any that the reducer refuses.
 *
 * Unlike `applyLog`, which is all-or-nothing because a half-applied *server*
 * batch is a corrupt replica, this is per-command: the outbox is a queue of
 * independent local intentions, and one that has been overtaken by events
 * should not discard the others.
 *
 * @param base - The confirmed server state. Not mutated.
 * @param commands - The outbox, in order.
 */
export function replay(base: WorkspaceState, commands: readonly Command[]): ReplayResult {
  if (commands.length === 0) return { state: base, kept: [], dropped: [] };

  let state = base;
  const kept: Command[] = [];
  const dropped: DroppedCommand[] = [];
  for (const command of commands) {
    const result = applyCommand(state, command);
    if (result.ok) {
      state = result.state;
      kept.push(command);
    } else {
      dropped.push({ command, error: result.error });
    }
  }
  return { state, kept, dropped };
}

/**
 * Advance a confirmed state by a slice of the server log.
 *
 * Strict on purpose: these commands were already accepted by the server, so a
 * failure here means the client's `confirmed` is not the prefix it believes it
 * is. The caller's only correct response is to throw the prefix away and
 * re-sync from scratch, which is what {@link ConfirmResult.ok} `=== false`
 * tells it to do.
 *
 * @param confirmed - The workspace at `fromVersion`. Not mutated.
 * @param commands - Versions `fromVersion+1 … toVersion`, in order.
 */
export function confirm(confirmed: WorkspaceState, commands: readonly Command[]): ConfirmResult {
  let state = confirmed;
  for (const command of commands) {
    const result = applyCommand(state, command);
    if (!result.ok) return { ok: false, error: result.error };
    state = result.state;
  }
  return { ok: true, state };
}

/** @see confirm */
export type ConfirmResult =
  | { readonly ok: true; readonly state: WorkspaceState }
  | { readonly ok: false; readonly error: CommandError };

/**
 * Whether a workspace holds anything a user would miss.
 *
 * Built-in equipment profiles do not count — every fresh workspace has them, so
 * counting them would make "is this empty?" always false and would turn the
 * guest-upload rule (see `sync-client.ts`) into "always upload".
 */
export function isEmptyWorkspace(state: WorkspaceState): boolean {
  if (Object.keys(state.recipes).length > 0) return false;
  if (Object.keys(state.blends).length > 0) return false;
  return Object.values(state.equipmentProfiles).every((profile) => profile.builtin);
}

/**
 * Whether two workspaces are the same document, by value.
 *
 * Used by the two paths that recompute the screen speculatively — a cross-tab
 * ping (`workspace-context.tsx`) and the first catch-up of a session
 * (`sync-client.ts`) — to decide whether anything actually moved. Both are
 * asking it to save an undo stack `replaceState` would otherwise clear. A false
 * negative — two equal documents whose keys were serialised in a different
 * order — costs one needless `replaceState`, which is what both paths did
 * before, so the comparison is allowed to be simple.
 */
export function sameWorkspace(a: WorkspaceState, b: WorkspaceState): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}
